import {
  formatClock,
  formatISWTLevelLabel,
  normalizeISWTPayload,
  resolveISWTInterpretation,
  summarizeISWTResult,
  type ISWTResultPayload,
} from '../shuttle-walk/shuttleWalkDomain';
import { formatBorgValue } from '../step-tests/stepTestsDomain';

export const getISWTSeverityLabel = (payload: unknown): string => summarizeISWTResult(payload).severity;

export const getISWTSeverityTone = (payload: unknown): string => {
  const summary = summarizeISWTResult(payload);
  if (summary.severity === 'Atenção de segurança') return 'bg-rose-100 text-rose-700';
  if (summary.severity === 'Protocolo parcial') return 'bg-amber-100 text-amber-700';
  if (summary.severity === 'Completou todos os níveis') return 'bg-emerald-100 text-emerald-700';
  return 'bg-sky-100 text-sky-700';
};

export const getISWTScoreColor = (payload: unknown): string => {
  const severity = summarizeISWTResult(payload).severity;
  if (severity === 'Atenção de segurança') return '#dc2626';
  if (severity === 'Protocolo parcial') return '#f59e0b';
  if (severity === 'Protocolo completo' || severity === 'Completou todos os níveis') return '#10b981';
  return '#2e5a88';
};

interface ISWTResultRendererProps {
  payload: ISWTResultPayload | null | undefined;
}

const SummaryMetric = ({ label, value, note }: { label: string; value: string; note?: string | null }) => (
  <div className="rounded-[24px] border border-slate-100 bg-white p-5 shadow-sm">
    <div className="text-[10px] font-black uppercase tracking-[0.22em] text-slate-400">{label}</div>
    <div className="mt-2 text-3xl font-black text-slate-900">{value}</div>
    {note ? <div className="mt-2 text-sm text-slate-500">{note}</div> : null}
  </div>
);

export default function ISWTResultRenderer({ payload }: ISWTResultRendererProps) {
  const normalized = normalizeISWTPayload(payload);
  const summary = summarizeISWTResult(payload);
  const interpretation = resolveISWTInterpretation(payload);
  const scoreColor = getISWTScoreColor(payload);

  if (!normalized) {
    return <div className="rounded-[24px] border border-slate-100 bg-white p-6 text-sm text-slate-600">Sem payload válido para o ISWT.</div>;
  }

  return (
    <div className="space-y-6">
      <div className="grid gap-6 md:grid-cols-5">
        <div className="rounded-[28px] border border-slate-200 bg-white p-8 text-center shadow-sm md:col-span-2">
          <p className="mb-4 text-xs font-black uppercase tracking-[0.25em] text-primary-700">Incremental shuttle walk test</p>
          <div className="text-6xl font-black leading-none" style={{ color: scoreColor }}>{summary.bestDistanceMeters}</div>
          <div className="mt-2 text-sm font-semibold text-slate-700">metros na melhor tentativa</div>
          <div className="mt-5 inline-flex items-center gap-2 text-sm font-bold" style={{ color: scoreColor }}>
            <span className="h-2 w-2 rounded-full bg-current" aria-hidden="true" />
            {summary.severity}
          </div>
          <div className="mt-3 text-xs font-medium text-slate-500">
            {summary.predictedDistanceMeters != null && summary.percentPredicted != null
              ? `${summary.percentPredicted.toFixed(1)}% do previsto`
              : 'Sem valor previsto'}
          </div>
        </div>

        <div className="flex flex-col justify-center space-y-4 md:col-span-3">
          <div>
            <h4 className="text-2xl font-black tracking-tight text-slate-800">Interpretação</h4>
            <p className="mt-3 max-w-3xl text-sm leading-relaxed text-slate-600">{interpretation}</p>
          </div>
          <div className="grid gap-3 text-sm text-slate-600 md:grid-cols-2">
            <div className="rounded-2xl border border-slate-200 bg-slate-50 px-4 py-3">
              Melhor nível: <span className="font-semibold text-slate-900">{summary.bestLevelLabel}</span>
            </div>
            <div className="rounded-2xl border border-slate-200 bg-slate-50 px-4 py-3">
              Tentativas registradas: <span className="font-semibold text-slate-900">{summary.attemptCount}</span>
            </div>
          </div>
        </div>
      </div>

      <div className="grid gap-6 md:grid-cols-2 xl:grid-cols-5">
        <SummaryMetric label="Melhor distância" value={`${summary.bestDistanceMeters} m`} note={summary.bestAttemptNumber != null ? `Tentativa ${summary.bestAttemptNumber}` : null} />
        <SummaryMetric label="Velocidade pico" value={summary.bestSpeedKmh != null ? `${summary.bestSpeedKmh.toFixed(2)}` : '—'} note="km/h" />
        <SummaryMetric label="Previsto" value={summary.predictedDistanceMeters != null ? `${summary.predictedDistanceMeters.toFixed(1)} m` : '—'} note="Probst et al. 2011" />
        <SummaryMetric label="SpO2 mínima" value={summary.minSpo2 != null ? `${summary.minSpo2.toFixed(0)}%` : '—'} note={summary.peakHr != null ? `FC pico ${summary.peakHr.toFixed(0)} bpm` : null} />
        <SummaryMetric label="Delta T2-T1" value={summary.secondAttemptDeltaMeters != null ? `${summary.secondAttemptDeltaMeters} m` : '—'} note={summary.learningEffectDetected ? 'Sugere efeito de aprendizagem' : 'Sem comparação'} />
      </div>

      <div className="rounded-[24px] border border-slate-100 bg-white p-6 shadow-sm">
        <div className="text-[10px] font-black uppercase tracking-[0.24em] text-slate-400">Protocolo registrado</div>
        <div className="mt-4 grid gap-3 text-sm text-slate-700 md:grid-cols-2">
          <div className="rounded-2xl border border-slate-200 bg-slate-50 px-4 py-3">Pista reta de 10 m</div>
          <div className="rounded-2xl border border-slate-200 bg-slate-50 px-4 py-3">12 níveis de 1 minuto</div>
          <div className="rounded-2xl border border-slate-200 bg-slate-50 px-4 py-3">Incremento de 0,17 m/s por estágio</div>
          <div className="rounded-2xl border border-slate-200 bg-slate-50 px-4 py-3">Pacing auditivo sintetizado no navegador</div>
        </div>
      </div>

      <div className="overflow-hidden rounded-[24px] border border-slate-100 shadow-sm">
        <table className="w-full border-collapse text-left text-sm">
          <thead>
            <tr className="bg-slate-50 text-[10px] font-black uppercase tracking-[0.22em] text-slate-400">
              <th className="px-5 py-4">Tentativa</th>
              <th className="px-5 py-4">Tempo</th>
              <th className="px-5 py-4">Distância</th>
              <th className="px-5 py-4">Nível</th>
              <th className="px-5 py-4">SpO2 min</th>
              <th className="px-5 py-4">FC pico</th>
              <th className="px-5 py-4">Borg D/MMII</th>
              <th className="px-5 py-4">Encerramento</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100 bg-white">
            {normalized.attempts.map((attempt) => (
              <tr key={`iswt-attempt-${attempt.attemptNumber}`}>
                <td className="px-5 py-4 font-bold text-slate-700">{attempt.attemptNumber}</td>
                <td className="px-5 py-4 text-slate-700">{formatClock(attempt.timer.elapsed_s)}</td>
                <td className="px-5 py-4 font-semibold text-slate-900">{attempt.distanceMeters} m</td>
                <td className="px-5 py-4 text-slate-700">{formatISWTLevelLabel(attempt.completedLevel, attempt.completedShuttlesOnLevel)}</td>
                <td className="px-5 py-4 text-slate-700">{attempt.observedMinSpo2 != null ? `${attempt.observedMinSpo2.toFixed(0)}%` : '—'}</td>
                <td className="px-5 py-4 text-slate-700">{attempt.observedPeakHr != null ? `${attempt.observedPeakHr.toFixed(0)}` : '—'}</td>
                <td className="px-5 py-4 text-slate-700">
                  {formatBorgValue(attempt.final.borg.dyspnea)} / {formatBorgValue(attempt.final.borg.legFatigue)}
                </td>
                <td className="px-5 py-4 text-slate-700">{attempt.stopReason}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
